import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "../utils/api";
// crypto.js exists in utils but encryption is not applied

const useChatStore = create(
  persist(
    (set, get) => ({
      conversations: [],
      activeConversation: null,
      messages: [],
      onlineUsers: [],
      typingUsers: {},          // typing state
      groupActivities: [],
      unreadCounts: {},         // per-conversation unread counts
      mutedConversations: [],   // muted conversation IDs
      isLoadingConversations: false,
      isLoadingMessages: false,
      conversationError: null,

      // ── Online ────────────────────────────────────────────────────────────
      setOnlineUsers: (users) => set({ onlineUsers: users }),

      // ── Mute ─────────────────────────────────────────────────────────────
      muteConversation: (conversationId) => {
        set((state) => {
          const muted = state.mutedConversations || [];
          const next = muted.includes(conversationId)
            ? muted.filter((id) => id !== conversationId)
            : [...muted, conversationId];
          return { mutedConversations: next };
        });
      },

      isConversationMuted: (conversationId) =>
        (get().mutedConversations || []).includes(conversationId),

      // ── Conversations ─────────────────────────────────────────────────────
      fetchConversations: async () => {
        const hasCached = get().conversations.length > 0;
        if (!hasCached) set({ isLoadingConversations: true });
        try {
          const res = await api.get("/conversations");
          set({
            conversations: res.data.conversations || [],
            isLoadingConversations: false,
            conversationError: null,
          });
        } catch (err) {
          set({ isLoadingConversations: false, conversationError: err.message });
          // Auto retry after 3 seconds if it was a network error
          if (!err.response) {
            setTimeout(() => get().fetchConversations(), 3000);
          }
        }
      },

      setActiveConversation: async (conversation) => {
        set((state) => ({
          activeConversation: conversation,
          messages: [],
          unreadCounts: { ...state.unreadCounts, [conversation._id]: 0 },
        }));
        if (conversation) await get().fetchMessages(conversation._id);
      },

      // ── Messages ──────────────────────────────────────────────────────────
      fetchMessages: async (conversationId) => {
        // Show cached messages immediately if available
        const cached = get().messages;
        const hasCached = cached.length > 0 && get().activeConversation?._id === conversationId;
        if (!hasCached) set({ isLoadingMessages: true });
        try {
          const res = await api.get(`/messages/${conversationId}`);
          const rawMsgs = res.data.messages || [];

          const msgs = rawMsgs;

          set({ messages: msgs, isLoadingMessages: false });
        } catch (_) {
          set({ isLoadingMessages: false });
        }
      },

      sendMessage: async (conversationId, text, image, replyToId, fileData, gifUrl) => {
        try {
          let payload = { conversationId, text, image, replyToId, gifUrl, ...fileData };

          const res = await api.post("/messages", payload);
          const newMsg = res.data.message;
          const displayMsg = { ...newMsg, text: text || newMsg.text };
          set((state) => ({ messages: [...state.messages, displayMsg] }));
          get().updateConversationLastMessage(conversationId, displayMsg);
          return displayMsg;
        } catch (err) { throw err; }
      },

      sendVoiceMessage: async (conversationId, audioData, duration) => {
        try {
          const res = await api.post("/messages/voice", { conversationId, audioData, duration });
          const newMsg = res.data.message;
          set((state) => ({ messages: [...state.messages, newMsg] }));
          get().updateConversationLastMessage(conversationId, newMsg);
          return newMsg;
        } catch (err) { throw err; }
      },

      forwardMessage: async (messageId, conversationId) => {
        try {
          const res = await api.post(`/messages/${messageId}/forward`, { conversationId });
          const newMsg = res.data.message;
          if (get().activeConversation?._id === conversationId)
            set((state) => ({ messages: [...state.messages, newMsg] }));
          get().updateConversationLastMessage(conversationId, newMsg);
          return newMsg;
        } catch (err) { throw err; }
      },

      reactToMessage: async (messageId, emoji) => {
        try {
          const res = await api.post(`/messages/${messageId}/react`, { emoji });
          const { reactions } = res.data;
          get().updateMessageInState(messageId, { reactions });
          return { messageId, reactions };
        } catch (err) { throw err; }
      },

      togglePinMessage: async (conversationId, messageId) => {
        try {
          const res = await api.post(`/conversations/${conversationId}/pin/${messageId}`);
          const { pinnedMessages } = res.data;
          set((state) => ({
            activeConversation: state.activeConversation
              ? { ...state.activeConversation, pinnedMessages }
              : state.activeConversation,
            conversations: state.conversations.map((c) =>
              c._id === conversationId ? { ...c, pinnedMessages } : c),
          }));
          return res.data;
        } catch (err) { throw err; }
      },

      clearChat: async (conversationId) => {
        try {
          await api.delete(`/conversations/${conversationId}/clear`);
          set({ messages: [] });
        } catch (err) { throw err; }
      },

      leaveGroup: async (conversationId) => {
        try {
          const res = await api.delete(`/conversations/${conversationId}/leave`);
          // Always remove from our own sidebar since we left
          set((state) => ({
            conversations: state.conversations.filter((c) => c._id !== conversationId),
            activeConversation:
              state.activeConversation?._id === conversationId ? null : state.activeConversation,
          }));
          return res.data;
        } catch (err) { throw err; }
      },

      updateGroup: async (conversationId, data) => {
        try {
          const res = await api.put(`/conversations/${conversationId}`, data);
          const updated = res.data.conversation;
          set((state) => ({
            conversations: state.conversations.map((c) => c._id === conversationId ? updated : c),
            activeConversation:
              state.activeConversation?._id === conversationId ? updated : state.activeConversation,
          }));
          return updated;
        } catch (err) { throw err; }
      },

      addGroupMember: async (conversationId, userId) => {
        try {
          const res = await api.post(`/conversations/${conversationId}/participants`, { userId });
          const updated = res.data.conversation;
          set((state) => ({
            conversations: state.conversations.map((c) => c._id === conversationId ? updated : c),
            activeConversation:
              state.activeConversation?._id === conversationId ? updated : state.activeConversation,
          }));
          return updated;
        } catch (err) { throw err; }
      },

      removeGroupMember: async (conversationId, userId) => {
        try {
          const res = await api.delete(`/conversations/${conversationId}/participants/${userId}`);
          const updated = res.data.conversation;
          set((state) => ({
            conversations: state.conversations.map((c) => c._id === conversationId ? updated : c),
            activeConversation:
              state.activeConversation?._id === conversationId ? updated : state.activeConversation,
          }));
          return updated;
        } catch (err) { throw err; }
      },

      promoteToAdmin: async (conversationId, userId) => {
        try {
          const res = await api.post(`/conversations/${conversationId}/promote/${userId}`);
          const updated = res.data.conversation;
          set((state) => ({
            conversations: state.conversations.map((c) => c._id === conversationId ? updated : c),
            activeConversation:
              state.activeConversation?._id === conversationId ? updated : state.activeConversation,
          }));
          return updated;
        } catch (err) { throw err; }
      },

      blockUser: async (userId) => {
        try { await api.post(`/users/${userId}/block`); return true; }
        catch (err) { throw err; }
      },

      unblockUser: async (userId) => {
        try { await api.delete(`/users/${userId}/block`); return true; }
        catch (err) { throw err; }
      },

      // ── REAL-TIME: receiveMessage (original logic) ─────────────────────────
      receiveMessage: (message) => {
        const { activeConversation, messages } = get();
        if (activeConversation?._id === message.conversationId) {
          // Chat is open — add message immediately
          set({ messages: [...messages, message] });
          // Send read receipt
          if (useChatStore._emitRead) useChatStore._emitRead(message.conversationId);
        } else {
          // Chat is not open — increment unread badge (skip if muted)
          const isMuted = (get().mutedConversations || []).includes(message.conversationId);
          if (!isMuted) {
            set((state) => ({
              unreadCounts: {
                ...state.unreadCounts,
                [message.conversationId]: (state.unreadCounts[message.conversationId] || 0) + 1,
              },
            }));
          }
        }
        get().updateConversationLastMessage(message.conversationId, message);
      },

      // FIX 5: setEmitRead was missing — its absence crashed the entire useEffect
      setEmitRead: (fn) => { useChatStore._emitRead = fn; },

      markConversationRead: (conversationId, readerId) => {
        set((state) => ({
          messages: state.messages.map((m) => {
            if (m.conversationId !== conversationId) return m;
            if (m.readBy?.includes(readerId)) return m;
            return { ...m, readBy: [...(m.readBy || []), readerId] };
          }),
        }));
      },

      // ── Conversation helpers ───────────────────────────────────────────────
      updateConversationLastMessage: (conversationId, message) => {
        set((state) => ({
          conversations: state.conversations
            .map((c) =>
              c._id === conversationId
                ? { ...c, lastMessage: message, lastMessageAt: message.createdAt }
                : c
            )
            .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt)),
        }));
      },

      updateConversationInState: (conversationId, updates) => {
        // If updates contains _id it's a full conversation object — replace entirely
        // Otherwise it's a partial patch — spread merge
        const isFull = updates && updates._id;
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c._id === conversationId ? (isFull ? updates : { ...c, ...updates }) : c),
          activeConversation:
            state.activeConversation?._id === conversationId
              ? (isFull ? updates : { ...state.activeConversation, ...updates })
              : state.activeConversation,
        }));
      },

      addConversation: (conversation) => {
        set((state) => {
          const exists = state.conversations.find((c) => c._id === conversation._id);
          if (exists) return {};
          return { conversations: [conversation, ...state.conversations] };
        });
      },

      // ── Message helpers ────────────────────────────────────────────────────
      deleteMessageFromState: (messageId, deletedForEveryone = false) => {
        if (deletedForEveryone) {
          set((state) => ({
            messages: state.messages.map((m) =>
              m._id === messageId
                ? { ...m, isDeletedForEveryone: true, text: "", image: "", file: "", gifUrl: "", voiceUrl: "" }
                : m),
          }));
        } else {
          set((state) => ({ messages: state.messages.filter((m) => m._id !== messageId) }));
        }
      },

      updateMessageInState: (messageId, updates) => {
        set((state) => ({
          messages: state.messages.map((m) => m._id === messageId ? { ...m, ...updates } : m),
        }));
      },

      // ── REAL-TIME: Typing (original logic) ────────────────────────────────
      setTyping: (conversationId, userId, userName, isTyping) => {
        set((state) => {
          const key = `${conversationId}-${userId}`;
          const updated = { ...state.typingUsers };
          if (isTyping) {
            updated[key] = { conversationId, userId, userName };
          } else {
            delete updated[key];
          }
          return { typingUsers: updated };
        });
      },

      getTypingInConversation: (conversationId) => {
        const { typingUsers } = get();
        return Object.values(typingUsers).filter((t) => t.conversationId === conversationId);
      },

      // ── Group activities ───────────────────────────────────────────────────
      addGroupActivity: (conversationId, text) => {
        const activity = { id: Date.now() + Math.random(), conversationId, text, time: new Date() };
        set((state) => ({ groupActivities: [...state.groupActivities, activity] }));
        setTimeout(() => {
          set((state) => ({
            groupActivities: state.groupActivities.filter((a) => a.id !== activity.id),
          }));
        }, 10000);
      },

      // ── Reset on logout ────────────────────────────────────────────────────
      resetStore: () => set({
        conversations: [], activeConversation: null, messages: [],
        onlineUsers: [], typingUsers: {}, groupActivities: [],
        unreadCounts: {}, mutedConversations: [],
      }),
    }),
    {
      name: "chatify-unread",
      partialize: (state) => ({
        unreadCounts: state.unreadCounts,
        mutedConversations: state.mutedConversations,
      }),
    }
  )
);

// Module-level read emitter — not persisted to localStorage
useChatStore._emitRead = null;

export default useChatStore;
